import { useEffect, useState } from 'react';
import moment from 'moment';

import { Button, Input, Select, Form, Alert } from 'antd';

import { ErrorResponse } from 'utils/types';
import notifier from 'utils/notifier';
import { sortByIPA, selectDefaultProvider } from 'utils/helpers';
import { ProviderType } from 'utils/enums';

import FloatLabel from 'components/Common/FloatLabel';
import DatePickerLocalised from 'components/Common/DatePickerLocalised';

import './index.scss';

const { Option } = Select;

interface SearchMemberFormProps {
  getClaimReadWrite(userId: string): any,
  isFetchingClaimReadWrite: boolean,
  search(searchObject: Object): any,
  isSearching: boolean,
  setDataSource(data: any[]): void,
  provider: any,
  setProvider(provider: any): void,
  dos: string,
  setDos(dos: string): void,
  setAccessErrors(errors: string[]): void,
  accessErrors: string[];
}

const SearchMemberForm = (props: SearchMemberFormProps) => {

  const [birth, setBirth] = useState('');
  const [providerList, setProviderList] = useState<any[]>([]);
  const [form] = Form.useForm();
  const [showHelpfulText, setShowHelpfulText] = useState(false);

  useEffect(() => {
    getClaimReadProviderList();
  }, []);

  useEffect(() => {
    if (birth || props.dos) form.validateFields();
  }, [birth, props.dos]);

  const getClaimReadProviderList = () => {
    return props.getClaimReadWrite(sessionStorage.getItem('user_id')!)
      .then((response: any[]) => {
        if(response.length !== 0) {
          //sets the provider dropdown to the login user as default provider
          form.setFieldsValue({
            provider: selectDefaultProvider(response, undefined, 'string')
          });

          // sets provider props object to default provider
          props.setProvider(selectDefaultProvider(response, {}, 'string'));

          setProviderList(sortByIPA(response, 'Provider'));
        } else {
          //if the response is an empty array, that means we don't have any associated providers with claims write access.
          notifier.error('Oh no!', 'None of the providers associated with this User has write access to the claim module. No claim can be created.');
        }
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  };


  const validationRules = async (fieldData: any, value: any) => {
    const invalidDate = (value: any) => (fieldData.field === 'birth' || fieldData.field === 'dos') && value && moment(value).year() < 1900 && (!moment(value).isValid() || !value.match(/^\d{2}\/\d{2}\/\d{4}$/));
    const dosBeforeFiveYears = () => moment(props.dos).isBefore(moment().subtract(5, 'years'));
    const dosBeforeToday = () => moment(props.dos).isAfter(moment(), 'day');
    const charLessThanFive = (value: any) => fieldData.field === 'memberId' && value && value.trim().length < 5;

    if (form.getFieldValue('provider')) {
      if (charLessThanFive(value)) {
        return Promise.reject('Please enter at least 5 characters.');
      }

      if (invalidDate(value) ) {
        return Promise.reject(new Error('Invalid Date'));
      }

      // PCP (P) / Clinic (C) validation
      if ([ProviderType.PCP, ProviderType.CLINIC].includes(props.provider.RmiClass)) {

        const allFieldsEmpty = () => {
          return !form.getFieldValue('dos') && !form.getFieldValue('firstName') && !form.getFieldValue('lastName') && !form.getFieldValue('memberId');
        }

        const allFieldsResolved = () => {
          return (form.getFieldValue('dos') && form.getFieldValue('memberId')) ||
            (form.getFieldValue('dos') && form.getFieldValue('firstName') && form.getFieldValue('lastName'));
        }

        const missingThreeFields = () => {
          return (!form.getFieldValue('firstName') && !form.getFieldValue('lastName') && !form.getFieldValue('memberId')) && form.getFieldValue('dos');
        }

        if (allFieldsResolved()) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }

        if (fieldData.field === 'dos') {
          const dosResolved = () => {
            return form.getFieldValue('dos');
          }

          if (dosBeforeFiveYears()) {
            return Promise.reject(new Error('Date of service cannot be older than 5 years.'));
          }

          if (missingThreeFields()) {
            setShowHelpfulText(true);
          }

          if (dosBeforeToday()) {
            return Promise.reject(new Error('Please enter a date of service that is today or earlier.'));
          }

          else if (allFieldsEmpty()) {
            setShowHelpfulText(true);
            return Promise.reject('Required');
          }

          else if (dosResolved()) {
            setShowHelpfulText(false);
            return Promise.resolve();
          }

          else if ((form.getFieldValue('firstName') || form.getFieldValue('lastName') || !form.getFieldValue('memberId')) || !form.getFieldValue('dos')) {
            setShowHelpfulText(false);
            return Promise.reject('Required')
          }
        }

        if (fieldData.field === 'memberId') {

          const memberIdResolved = () => {
            return form.getFieldValue('memberId');
          }
          if (allFieldsEmpty()) {
            setShowHelpfulText(true);
            return Promise.reject('Required');
          }

          else if (memberIdResolved()) {
            setShowHelpfulText(false);
            return Promise.resolve();
          }

          else if (!form.getFieldValue('memberId') && !(form.getFieldValue('firstName') || form.getFieldValue('lastName'))) {
            setShowHelpfulText(false);
            return Promise.reject('Required');
          }
        }

        if (fieldData.field === 'firstName') {

          const firstNameResolved = () => {
            return (form.getFieldValue('firstName') || form.getFieldValue('lastName'));
          }

          const missing_firstName_memberId = () => {
            return !form.getFieldValue('firstName') && form.getFieldValue('lastName') && !form.getFieldValue('memberId');
          }

          if (allFieldsEmpty()) {
            setShowHelpfulText(true);
            return Promise.reject('Required');
          }

          else if (missing_firstName_memberId()) {
            setShowHelpfulText(false);
            return Promise.reject('Required');
          }

          else if (firstNameResolved()) {
            setShowHelpfulText(false);
            return Promise.resolve();
          }
        }

        if (fieldData.field === 'lastName') {
          const lastNameResolved = () => {
            return (form.getFieldValue('firstName') || form.getFieldValue('lastName'));
          }

          const missing_lastName_memberId = () => {
            return form.getFieldValue('firstName') && !form.getFieldValue('lastName') && !form.getFieldValue('memberId');
          }

          if (allFieldsEmpty()) {
            setShowHelpfulText(true);
            return Promise.reject('Required');
          }

          else if (missing_lastName_memberId()) {
            return Promise.reject('Required');
          }

          else if (lastNameResolved()) {
            setShowHelpfulText(false);
            return Promise.resolve();
          }
        }

      } else {
        //specialist validation and other than provider
        const allFieldsResolved = () => {
          return (form.getFieldValue('memberId') && form.getFieldValue('birth') && form.getFieldValue('dos')) ||
          (form.getFieldValue('firstName') && form.getFieldValue('lastName') && form.getFieldValue('birth') && form.getFieldValue('dos'));
        }

        const allFieldsEmpty = () => {
          return !form.getFieldValue('memberId') && !form.getFieldValue('dos') && !form.getFieldValue('birth') && !form.getFieldValue('lastName') && !form.getFieldValue('firstName');
        }

        if (allFieldsResolved()) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }

        else if (allFieldsEmpty()) {
          setShowHelpfulText(true);
          return Promise.reject('Required');
        }

        if (fieldData.field === 'dos') {
          const missingFourFields = () => {
            return !form.getFieldValue('firstName') && !form.getFieldValue('lastName') && !form.getFieldValue('memberId') && !form.getFieldValue('birth') && form.getFieldValue('dos');
          }

          const twoConditionsNotMet = () => {
            return (form.getFieldValue('firstName') || form.getFieldValue('lastName') || !form.getFieldValue('memberId')) || !form.getFieldValue('birth') || !form.getFieldValue('dos');
          }

          setShowHelpfulText(false);

          if (missingFourFields()) {
            setShowHelpfulText(true);
          }

          if (dosBeforeFiveYears()) {
            return Promise.reject(new Error('Date of service cannot be older than 5 years.'));
          }

          if (dosBeforeToday()) {
            return Promise.reject(new Error('Please enter a date of service that is today or earlier.'));
          }

          if (form.getFieldValue('dos')) {
            return Promise.resolve();
          }

          if (twoConditionsNotMet()) {

            return Promise.reject('Required')
          }
        }

        if (fieldData.field === 'memberId') {
          setShowHelpfulText(false);
          const missingTwoFields = () => {
            return !form.getFieldValue('memberId') && !(form.getFieldValue('firstName') || form.getFieldValue('lastName'));
          }

          if (missingTwoFields()) {
            return Promise.reject('Required');
          }
        }

        if (fieldData.field === 'firstName') {

          const firstNameResolved = () => {
            return form.getFieldValue('firstName') && form.getFieldValue('lastName');
          }

          const missing_lastName_memberId = () => {
            return (!form.getFieldValue('firstName') || form.getFieldValue('lastName')) && !form.getFieldValue('memberId');
          }

          setShowHelpfulText(false);

          if (firstNameResolved()) {
            return Promise.resolve();
          }

          if (missing_lastName_memberId()) {
            return Promise.reject('Required');
          }
        }

        if (fieldData.field === 'lastName') {
          const lastNameResolved = () => {
            return form.getFieldValue('firstName') && form.getFieldValue('lastName');
          }

          const missingLastNameOrMemberId = () => {
            return (form.getFieldValue('firstName') || !form.getFieldValue('lastName')) && !form.getFieldValue('memberId');
          }

          setShowHelpfulText(false);

          if (lastNameResolved()) {
            return Promise.resolve();
          }

          if (missingLastNameOrMemberId()) {
            return Promise.reject('Required');
          }
        }

        if (fieldData.field === 'birth') {
          const missing_dob_memberId_dos = () => {
            return (form.getFieldValue('firstName') || form.getFieldValue('lastName') || !form.getFieldValue('memberId')) || !form.getFieldValue('birth') || !form.getFieldValue('dos');
          }

          setShowHelpfulText(false);

          if (form.getFieldValue('birth')) {
            return Promise.resolve();
          }

          if (missing_dob_memberId_dos()) {
            return Promise.reject('Required')
          }
        }
      }

    } else {
      setShowHelpfulText(false);
    }
  };

  const onSearch = () => {
    form.validateFields().then((values: any) => {

      const provider = JSON.parse(values.provider);
      let formData = {
        CompanyId: provider.RmiCompanyId,
        DateOfBirth: birth,
        DateOfService: props.dos,
        Provider: provider,
        ProvKeyId: provider.RmiProvKeyId,
        FirstName: values.firstName,
        LastName: values.lastName,
        UserId: sessionStorage.getItem('user_id'),
        MemberId: values.memberId,
        index: 743,
      };
      localStorage.setItem("print_ipa", provider.RmiCompanyId);
      return props.search(formData)
        .then((response: any) => {
          setShowHelpfulText(false);
          if (response.length === 0) {
            notifier.error(' There were no results for this search. Please check the fields and try again.');
          }
          props.setDataSource(response);
          props.setAccessErrors([]);
        })
        .catch((err: ErrorResponse) => {
          notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
        });
    });
  };

  const onReset = () => {
    form.resetFields();
    props.setProvider({});
    setBirth('');
    props.setDataSource([]);
    props.setDos('');

    form.resetFields();
    setShowHelpfulText(false);
    props.setAccessErrors([]);
  };

  return (
    <Form
      form={form}
      name="claimSearchMember"
      onFinish={onSearch}
    >
      <div className="member-search row flex-lg-wrap justify-content-start mb-3 ml-0">
        <div className="col-lg-12 col-md-12">
          <FloatLabel
            name="provider"
            label="Provider"
            form={form}
            value={props.provider}
            validateTrigger={['onSubmit']}
            rules={[{ required: true, message: 'Please select provider.' }]}
          >
          <Select
            showSearch
            value={props.provider}
            placeholder="Select a Provider..."
            onChange={value => props.setProvider(JSON.parse(value))}
            loading={props.isFetchingClaimReadWrite}
          >
            {providerList.map((option: any, key: number) => {
              return <Option key={key} value={JSON.stringify(option)}>{option.Provider}</Option>;
            })}
          </Select>
          </FloatLabel>
        </div>
        <div className="col-lg-6 col-md-6">
          <FloatLabel
            name="memberId"
            label="Member ID"
            form={form}
            validateTrigger={['onSubmit']}
            dependencies={['firstName', 'lastName', 'birth', 'dos']}
            rules={[
              () => ({
                validator: validationRules
              })
            ]}
          >
          <Input />
          </FloatLabel>
        </div>

        <div className="col-lg-6 col-md-6">
          <FloatLabel
            name="birth"
            label="DOB (MM/DD/YYYY)"
            value={birth}
            validateTrigger={['onSubmit']}
            dependencies={['firstName', 'lastName', 'memberId', 'dos']}
            form={form}
            rules={[
              () => ({
                validator: validationRules
              }),
            ]}
          >
          <DatePickerLocalised
            date={birth}
            setDate={setBirth}
            formInstance={form}
            formItemName={'birth'}
          />
          </FloatLabel>
        </div>

        <div className="col-lg-6 col-md-6">
          <FloatLabel
            name="lastName"
            label="Last Name"
            form={form}
            validateTrigger={['onSubmit']}
            dependencies={['firstName', 'memberId', 'birth', 'dos']}
            rules={[
              () => ({
                validator: validationRules
              })
            ]}
          >
          <Input
          />
          </FloatLabel>
        </div>
        <div className="col-lg-6 col-md-6">
          <FloatLabel
            name="firstName"
            label="First Name"
            form={form}
            validateTrigger={['onSubmit']}
            dependencies={['memberId', 'lastName', 'birth', 'dos']}
            rules={[
              () => ({
                validator: validationRules
              })
            ]}
          >
          <Input
          />
          </FloatLabel>
        </div>

        <div className="col-lg-6 col-md-6">
          <FloatLabel
            name="dos"
            label="DOS (MM/DD/YYYY)"
            value={props.dos}
            validateTrigger={['onSubmit']}
            dependencies={['memberId', 'lastName', 'birth', 'firstName']}
            form={form}
            rules={[
              () => ({
                validator: validationRules
              }),
            ]}
          >
          <DatePickerLocalised
            date={props.dos}
            setDate={props.setDos}
            formInstance={form}
            formItemName={'dos'}
          />
          </FloatLabel>
        </div>

        <div className="col-lg-6">
          <Form.Item>
            <div className="btn-wrapper">
              <Button type="primary" style={{ width: '100%' }} htmlType="submit" loading={props.isSearching}>SEARCH</Button>
              <Button type="default" style={{ width: '100%' }} onClick={onReset}>RESET</Button>
            </div>
          </Form.Item>
        </div>

        {showHelpfulText && (
          <Alert
            message={[ProviderType.PCP, ProviderType.CLINIC].includes(props.provider.RmiClass) ?
              <>
                Please meet one of the following requirements:<br />
                MemberID + DOS (mm/dd/yyyy) <br />
                Last Name + First Name + DOS (mm/dd/yyyy)
              </>
                :
              <>
                Please meet one of the following requirements:<br />
                Member ID + DOB (mm/dd/yyyy) + DOS (mm/dd/yyyy) <br />
                Last Name + First Name + DOB (mm/dd/yyyy) + DOS (mm/dd/yyyy)
              </>
            }
            type="error"
            className="col-lg-24"
          />
        )}

        {props.accessErrors?.length > 0 ? (
          <Alert
            message={
              <>
                {props.accessErrors.map((x: string) => {
                  return x;
                })}
              </>
            }
          />
        ) : null}
      </div>
    </Form>
  );
};

export default SearchMemberForm;